import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { contentApi } from '../services/content-api';
import type { ContentSummary } from '@echo-portal/shared';

/**
 * Spec 022 — asset library list hook.
 *
 * When `branchId` is set, returns ONLY the branch's asset rows. This
 * mirrors the guideline Library's `showBranchContent ? branchList :
 * publishedList` either/or pattern (Library.tsx:325-330) and is the
 * correct shape because branch creation already inherits every
 * accessible main row as a branch-scoped shadow via
 * `contentInheritanceService.inheritContent` — the branch list IS
 * the complete view of that user's asset catalogue.
 *
 * An earlier version of this hook MERGED published + branch lists
 * by `id`, but branch shadow rows have new UUIDs (with
 * `sourceContentId` pointing back at main) so the dedup never
 * collapsed them. Users on a draft branch saw EVERY asset twice —
 * once as the published main row and once as the inherited shadow.
 * The fix: when on a branch, the branch list is the only list.
 *
 * Without `branchId`, the hook hits the published list (the
 * canonical catalogue) — the standard read path for "Published".
 */
export interface UseAssetListParams {
  /** AND-combined tag filter. Already-lowercased values match the canonical store. */
  tags?: string[];
  categoryId?: string;
  subcategoryId?: string;
  page?: number;
  limit?: number;
  /** When set, also pull the asset rows from this branch and merge. */
  branchId?: string;
}

export interface UseAssetListResult {
  items: ContentSummary[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
}

export const assetListKeys = {
  all: ['assets'] as const,
  list: (params: UseAssetListParams) =>
    [...assetListKeys.all, 'list', params] as const,
  branchList: (branchId: string) =>
    [...assetListKeys.all, 'branch', branchId] as const,
};

export function useAssetList(params: UseAssetListParams = {}): UseAssetListResult {
  const { tags, categoryId, subcategoryId, page = 1, limit = 24, branchId } = params;

  const queries = useQueries({
    queries: [
      {
        queryKey: assetListKeys.list({ tags, categoryId, subcategoryId, page, limit }),
        queryFn: () =>
          contentApi.listPublished({
            contentType: 'asset',
            ...(tags && tags.length > 0 ? { tags } : {}),
            ...(categoryId ? { categoryId } : {}),
            ...(subcategoryId ? { subcategoryId } : {}),
            page,
            limit,
          }),
        // Only fetch the published list when we're NOT on a branch —
        // see the doc comment above for why merging is wrong.
        enabled: !branchId,
        staleTime: 30_000,
      },
      {
        queryKey: branchId
          ? assetListKeys.branchList(branchId)
          : ['assets', 'branch', 'none'],
        queryFn: () =>
          contentApi.listByBranch({
            branchId: branchId!,
            contentType: 'asset',
            limit: 100,
          }),
        enabled: !!branchId,
        staleTime: 30_000,
      },
    ],
  });

  const publishedQuery = queries[0];
  const branchQuery = queries[1];

  // When on a branch, the branch list is the complete view: it
  // contains every inherited shadow PLUS any branch-local new
  // uploads. Apply the client-side filters here so the branch list
  // honours the same scoping the published list does server-side.
  const items = useMemo(() => {
    // Feature 016 — locked stubs come back from the access filter
    // when a row is `discoverable=true` but the viewer doesn't have
    // access. They have most fields stripped (tags, contentType,
    // file, etc. are undefined) and would crash the AssetCard at
    // `asset.tags.slice(0, 1)`. Mirror the SubcategoryLandingPage
    // filter (which has carried this guard since spec 016) so the
    // grid only attempts to render full rows. Locked rows still
    // appear in the sidebar tree elsewhere with the lock-icon
    // affordance.
    const stripLocked = (rows: ContentSummary[]) =>
      rows.filter((r) => (r as { locked?: boolean }).locked !== true);

    if (branchId) {
      const branchItems = stripLocked(branchQuery.data?.items ?? []);
      return branchItems.filter((item) => {
        if (categoryId && item.categoryId !== categoryId) return false;
        if (subcategoryId && item.subcategoryId !== subcategoryId) return false;
        if (tags && tags.length > 0) {
          const itemTags = (item.tags ?? []).map((t) => t.toLowerCase());
          if (!tags.every((t) => itemTags.includes(t))) return false;
        }
        return true;
      });
    }
    return stripLocked(publishedQuery.data?.items ?? []);
  }, [
    branchId,
    branchQuery.data,
    publishedQuery.data,
    categoryId,
    subcategoryId,
    tags,
  ]);

  const activeQuery = branchId ? branchQuery : publishedQuery;

  return {
    items,
    total: branchId ? items.length : publishedQuery.data?.total ?? 0,
    page: publishedQuery.data?.page ?? page,
    limit: publishedQuery.data?.limit ?? limit,
    hasMore: branchId ? false : publishedQuery.data?.hasMore ?? false,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    error: activeQuery.error,
    refetch: () => {
      void activeQuery.refetch();
    },
  };
}

// Re-export so AssetLibrary can build other selective queries off the
// same key namespace (used by the published-only sidebar fetch).
export { useQuery };
